/**
 * AdminSidebar — navigation for the Admin portal.
 * Five grouped sections: Panoramica, Contenuti, Utenti, Sviluppo, Configurazione.
 */

import { Link, useLocation } from 'react-router-dom'
import {
  X,
  LayoutDashboard,
  Activity,
  FileText,
  Database,
  Cpu,
  Users,
  Shield,
  Clock,
  Bug,
  Key,
  BookOpen,
  Settings,
  CreditCard,
  Bell,
  BarChart3,
  LogOut,
  ChevronRight,
  Tag,
  Radio,
  Newspaper,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'

interface AdminSidebarProps {
  isOpen: boolean
  onClose: () => void
}

interface NavItem {
  label: string
  path: string
  icon: React.ElementType
  exact?: boolean
}

interface NavSection {
  title: string
  items: NavItem[]
}

const SECTIONS: NavSection[] = [
  {
    title: 'Panoramica',
    items: [
      { label: 'Dashboard', path: '/admin', icon: LayoutDashboard, exact: true },
      { label: 'Monitoring', path: '/admin/monitoring', icon: Activity },
    ],
  },
  {
    title: 'Contenuti',
    items: [
      { label: 'Bandi', path: '/admin/grants', icon: FileText },
      { label: 'Scraper Health', path: '/admin/scrapers', icon: Radio },
      { label: 'Mira Fonti', path: '/admin/mira-sources', icon: Newspaper },
      { label: 'Connettori', path: '/admin/connectors', icon: Database },
      { label: 'ML & Curation', path: '/admin/ml', icon: Cpu },
    ],
  },
  {
    title: 'Utenti',
    items: [
      { label: 'Gestione Utenti', path: '/admin/users', icon: Users },
      { label: 'Team', path: '/admin/teams', icon: Shield },
      { label: 'Activity Log', path: '/admin/activity', icon: Clock },
    ],
  },
  {
    title: 'Sviluppo',
    items: [
      { label: 'Debug & Log', path: '/admin/debug', icon: Bug },
      { label: 'API Keys', path: '/admin/api', icon: Key },
      { label: 'API Docs', path: '/admin/api-docs', icon: BookOpen },
    ],
  },
  {
    title: 'Configurazione',
    items: [
      { label: 'Impostazioni', path: '/admin/settings', icon: Settings },
      { label: 'Report', path: '/admin/reports', icon: BarChart3 },
      { label: 'Billing', path: '/admin/billing', icon: CreditCard },
      { label: 'Pricing Piani', path: '/admin/pricing', icon: Tag },
      { label: 'Notifiche', path: '/admin/notifications', icon: Bell },
    ],
  },
]

export function AdminSidebar({ isOpen, onClose }: AdminSidebarProps) {
  const location = useLocation()
  const { logout } = useAuth()

  const isActive = (item: NavItem) => {
    if (item.exact) return location.pathname === item.path
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/')
  }

  const linkClass = (item: NavItem) =>
    cn(
      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
      isActive(item)
        ? 'bg-primary text-primary-foreground'
        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
    )

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-screen w-64 border-r bg-background transition-transform lg:translate-x-0 lg:static',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Mobile close button */}
          <div className="flex h-16 items-center justify-between border-b px-4 lg:hidden">
            <span className="font-semibold">Menu Admin</span>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Desktop logo */}
          <div className="hidden h-16 items-center border-b px-4 lg:flex gap-2">
            <ChevronRight className="h-4 w-4 text-primary" />
            <Link
              to="/admin"
              className="text-lg font-bold tracking-tight text-primary"
              onClick={onClose}
            >
              Admin Console
            </Link>
          </div>

          {/* Nav sections */}
          <nav className="flex-1 space-y-4 overflow-y-auto p-4">
            {SECTIONS.map((section) => (
              <div key={section.title}>
                <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {section.title}
                </p>
                <div className="space-y-0.5">
                  {section.items.map((item) => {
                    const Icon = item.icon
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={linkClass(item)}
                        onClick={onClose}
                      >
                        <Icon className="h-4 w-4 shrink-0" />
                        <span>{item.label}</span>
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Logout */}
          <div className="border-t p-4">
            <button
              onClick={() => {
                onClose()
                logout()
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              <LogOut className="h-5 w-5 shrink-0" />
              <span>Esci</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  )
}
